<template>
  <div
    :id="`instruction_prospect_${instruction.id}`"
    class="relative flex flex-col py-3 pl-10 pr-3 border rounded min-h-12 bg-pure-white border-slate-400"
  >
    <div
      class="absolute flex flex-col items-center flex-shrink-0 rounded-l min-h-12 w-7"
      :class="{
        'py-3 justify-between':
          chevronOpened || instruction.instruction_type === 'todo',
        'justify-center': !chevronOpened
      }"
      :style="{
        height: 'calc(100% + 2px)',
        top: '-1px',
        left: '-1px',
        background: isCompleted
          ? 'linear-gradient(180deg, #F9FAFB 0%, #EAECF0 100%)'
          : isDueDateLate
          ? 'linear-gradient(180deg, #FFFAF5 0%, #FDDCAB 100%)'
          : 'linear-gradient(180deg, #F9FCE9 0%, #EEFDB1 100%)'
      }"
    >
      <div v-if="instruction.instruction_type === 'notes' && chevronOpened">
        <BlIcon
          icon="paper-note"
          :color="
            isCompleted ? 'slate-6' : isDueDateLate ? 'orange-6' : 'lime-6'
          "
          class="w-4 h-4"
        />
      </div>
      <div v-if="instruction.instruction_type === 'todo'">
        <input
          class="cursor-pointer"
          type="checkbox"
          :checked="instruction.is_checked"
          :name="`instruction_checkbox_${instruction.id}`"
          :id="`instruction_checkbox_${instruction.id}`"
          @change="onCheckInstruction($event.target.checked)"
        />
      </div>
      <div class="flex flex-col gap-1">
        <div
          v-if="editable && chevronOpened"
          class="h-5.5 w-5.5 flex items-center justify-center rounded cursor-pointer"
          :class="
            isCompleted
              ? 'hover:bg-slate-500'
              : isDueDateLate
              ? 'hover:bg-orange-500'
              : 'hover:bg-lime-400'
          "
          @click="editOpened = true"
        >
          <BlIcon
            icon="edit"
            :color="
              isCompleted ? 'slate-6' : isDueDateLate ? 'orange-6' : 'lime-6'
            "
            class="w-4 h-4"
          />
        </div>
        <div
          class="h-5.5 w-5.5 flex items-center justify-center rounded cursor-pointer"
          :class="
            isCompleted
              ? 'hover:bg-slate-500'
              : isDueDateLate
              ? 'hover:bg-orange-500'
              : 'hover:bg-lime-400'
          "
          @click="commentOpened = true"
        >
          <BlIcon
            v-if="showIconMessage"
            :icon="
              !instruction.comments.length ? 'message-text' : 'message-notif'
            "
            :color="
              isCompleted ? 'slate-6' : isDueDateLate ? 'orange-6' : 'lime-6'
            "
            class="w-4 h-4"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <div
        class="flex items-center justify-between gap-2 cursor-pointer"
        @click="chevronOpened = !chevronOpened"
      >
        <div class="flex truncate" :title="instruction.title">
          <BlTextSubtitle3 :text="instruction.title" class="truncate" />
        </div>
        <BlIcon
          v-if="showIcon"
          icon="chevron-down"
          class="w-5 h-5 transition-all duration-300"
          :style="{ rotate: chevronOpened ? '180deg' : '' }"
        />
      </div>
      <div v-if="chevronOpened" class="flex flex-col gap-1">
        <div class="flex">
          <div
            v-html="instruction.description"
            class="text-sm whitespace-pre-line text-slate-700"
          ></div>
        </div>
        <div
          v-if="chevronOpened && instruction.documents.length"
          class="grid grid-cols-2 gap-2 p-2 rounded bg-slate-200"
        >
          <BlFileHolder
            v-for="(doc, idx) in instruction.documents"
            :key="idx"
            :name="doc.blob.filename"
            :size="doc.blob.byte_size"
            :url="doc.file_url"
            :decimals="0"
          />
        </div>
      </div>
      <div
        v-if="instruction.instruction_type === 'todo'"
        class="flex flex-col gap-1"
      >
        <div class="flex">
          <div class="h-6.5 px-2 rounded-full bg-slate-200 truncate">
            <BlTextBody2 text="To: " />
            <BlTextSubtitle3 v-if="!instruction.assigned_user" text="-" />
            <BlTextSubtitle3
              v-else
              :text="instruction.assigned_user.name || 'Deleted user'"
              :title="instruction.assigned_user.name || 'Deleted user'"
              class="truncate"
              :class="{ 'italic ': !instruction.assigned_user.name }"
            />
          </div>
        </div>
        <div class="flex">
          <div v-if="isCompleted" class="h-6.5 px-2 rounded-full bg-slate-200">
            <BlTextBody2 text="Completed on" />
            <BlTextBody2
              :text="
                displayDate({
                  date: instruction.updated_at,
                  format: 'DD MMM YYYY, HH:mm'
                })
              "
            />
          </div>
          <div
            v-else
            class="h-6.5 px-2 rounded-full"
            :class="[isDueDateLate ? 'bg-orange-100' : 'bg-lime-100']"
          >
            <BlTextBody2
              text="Due date: "
              :class="[isDueDateLate ? 'text-orange-700' : 'text-lime-700']"
            />
            <BlTextSubtitle3
              v-if="!instruction.due_date"
              text="-"
              :class="[isDueDateLate ? 'text-orange-700' : 'text-lime-700']"
            />
            <BlTextSubtitle3
              v-else
              :text="
                displayDate({
                  date: instruction.due_date,
                  format: 'DD MMM YYYY, HH:mm'
                })
              "
              :class="[isDueDateLate ? 'text-orange-700' : 'text-lime-700']"
            />
          </div>
        </div>
      </div>
      <div v-if="chevronOpened" class="flex flex-col gap-2">
        <div>
          <BlInputButton
            v-if="!instruction.comments.length"
            type="plain"
            dense
            icon="plus"
            label="Add comment"
            @clicked="commentOpened = true"
          />
          <BlInputButton
            v-else
            type="plain"
            dense
            icon="message-notif"
            :label="`${instruction.comments.length} comment(s)`"
            @clicked="commentOpened = true"
          />
        </div>
        <div>
          <BlTextBody2 text="Created at" class="text-slate-600" />
          <BlTextBody2
            :text="
              displayDate({
                date: instruction.created_at,
                format: 'DD MMM YYYY, HH:mm'
              })
            "
            class="text-slate-600"
          />
        </div>
      </div>
    </div>
    <EditInstructionProspectPanel
      v-if="editable && instruction"
      :open="editOpened"
      :instruction="instruction"
      @close-panel="editOpened = false"
    />
    <CommentPanel
      v-if="instruction"
      :open="commentOpened"
      :instruction="instruction"
      @close-panel="commentOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import EditInstructionProspectPanel from "./EditInstructionProspectPanel.vue";
import CommentPanel from "./CommentPanel.vue";

import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlIcon from "../../../BlIcon.vue";
import BlBubbleCount from "../../../BlBubbleCount.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlFileHolder from "../../../BlFileHolder.vue";

export default {
  name: "InstructionProspect",
  components: {
    EditInstructionProspectPanel,
    CommentPanel,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlBubbleCount,
    BlInputButton,
    BlFileHolder
  },
  props: {
    instruction: { type: Object, required: true },
    editable: { type: Boolean, default: false }
    // @after-commit
  },
  data() {
    return {
      showIcon: false,
      showIconMessage: false,
      chevronOpened: false,
      editOpened: false,
      commentOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    isCompleted() {
      if (!this.instruction) return false;
      if (this.instruction.instruction_type !== "todo") return false;
      return this.instruction.is_checked;
    },
    isDueDateLate() {
      if (!this.instruction) return false;
      if (this.instruction.instruction_type !== "todo") return false;
      if (!this.instruction.due_date) return false;
      return moment(this.instruction.due_date).isBefore(moment());
    }
  },
  watch: {},
  mounted() {
    const id = this.instruction.id || 1;
    const timer = (id + 100) % 1000;
    this.showIcon = false;
    this.showIconMessage = false;
    setTimeout(() => {
      this.showIcon = true;
      setTimeout(() => {
        this.showIconMessage = true;
      }, timer);
    }, timer);
  },
  methods: {
    ...mapActions(["updateInstruction"]),
    displayDate,
    onCheckInstruction(check) {
      const data = {
        id: this.instruction.id,
        instruction: { is_checked: check }
      };
      this.updateInstruction(data).then(({ success }) => {
        if (!success) return;
        this.$emit("after-commit");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/prospectPanel/InstructionProspect.vue